import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// OpenLayers
import Geometry from 'ol/geom/Geometry';
import proj4 from 'proj4';
import { register as OlRegister } from 'ol/proj/proj4';
import OlLayerGroup from 'ol/layer/Group';
import OlSourceVector from 'ol/source/Vector';
import OlBaseLayer from 'ol/layer/Base';
import OlFeature from 'ol/Feature';
import OlCollection from 'ol/Collection';
import OlLayerVector from 'ol/layer/Vector';
import Map from '@/components/Map/Map';

// Custom
import { Controls, ZoomControl, ScaleLineControl, DisplayRouteControl, ZoomToExtentControl } from '@/components/Map/Controls';

import { Layers, VectorLayer } from '@/components/Map/Layers';
import { Overlays } from '@/components/Map/Overlays';
import { Interactions, DefaultInteractions, MouseWheelZoomInteraction } from '@/components/Map/Interactions';
import { poiStyle, ktStyle } from '@/components/Map/mapStyles';
import UserContext from '@/components/UserContext/UserContext';

import GeoAPILayers from '@/components/Map/Layers/GEO/GeoAPILayers';
import modelPOI from '@/models/poi';

import ClientLayer from '@/components/Map/Layers/ClientLayer';
import dataController from '@/lib/dataController';
import GeoAPIBaseLayers from '@/components/Map/Layers/GEO/GeoAPIBaseLayers';

// Types
import { DCRecord } from '@/@types/lib/dataController';
import { ViewOptionsType } from '@/@types/common/index';

// Services
import mapService from '@/services/mapService';
import useRouting from '@/components/MapRouting/useRouting';

type PublicRouteMapProps = {
  mapId?: number;
  initialRecordRoutePoints?: DCRecord[];
};

function PublicRouteMap(props: PublicRouteMapProps) {
  const userContext = useContext(UserContext);
  const { t } = useTranslation();

  const { getRouteExtent } = useRouting({});

  const mapId = props.mapId ? props.mapId : 3;

  const { initialRecordRoutePoints } = props;

  const [mapInitialized, setMapInitialized] = useState(false);

  const [poiSources, setPoiSources] = useState<Array<OlSourceVector<OlFeature<Geometry>>>>();

  const [baseLayersCollection, setBaseLayersCollection] = useState<Array<OlBaseLayer> | undefined>(undefined);

  const [viewOptions, setViewOptions] = useState<ViewOptionsType>({
    center: userContext?.mapSettings ? userContext.mapSettings.initial_view_center : [1731757, 5581737],
    zoom: userContext?.mapSettings ? userContext.mapSettings.initial_view_zoom : 8,
    extent: userContext?.mapSettings ? userContext.mapSettings.max_extent : undefined,
    // projection: wgs84PM,
    minZoom: 4,
    maxZoom: 17,
    constrainResolution: true,
  });

  const [layersCollection, setLayersCollection] = useState<OlCollection<OlBaseLayer> | undefined>(undefined);

  proj4.defs(
    'EPSG:3765',
    '+proj=tmerc +lat_0=0 +lon_0=16.5 +k=0.9999 +x_0=500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  );
  proj4.defs(
    'EPSG:3857',
    '+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext  +no_defs'
  );
  OlRegister(proj4);

  useEffect(() => {
    getLayers();
    refreshPOI();
  }, []);

  const getLayers = () => {
    mapService.getLayers(mapId).then((coll) => {
      setLayersCollection(coll);
      setMapInitialized(true);
    });
  };

  const createPOIFeatures = (records: DCRecord[]) => {
    return records.map((x) => {
      // @ts-ignore
      const f = x.geom as OlFeature<Geometry>;
      f.setId(x.id as number);
      f.set('poi_tip_id', x.poi_tip_id);
      f.set('naziv', x.naziv);
      f.set('ttoken', x.ttoken);
      return f;
    });
  };

  const createPOILayerVector = (id: string, token: string, source: OlSourceVector) => {
    const layer = new OlLayerVector({
      source,
      visible: true,
    });
    layer.set('id', id);
    layer.set('title', token);
    layer.set('icon', 'images/icons/dom_1.svg');
    return layer;
  };

  const dcPOI = new dataController(modelPOI, 'public/poi');

  const refreshPOI = () => {
    dcPOI.GetData().then((resp) => {
      if (resp.success) {
        if (Array.isArray(resp.data) && resp.data.length > 0) {
          const featuresPlaninarski = createPOIFeatures(
            resp.data.filter((x) => x.geom !== undefined && (x.poi_tip_id === 31 || x.poi_tip_id === 32))
          );
          const featuresSkloniste = createPOIFeatures(resp.data.filter((x) => x.geom !== undefined && x.poi_tip_id === 33));
          const featuresPenjaliste = createPOIFeatures(resp.data.filter((x) => x.geom !== undefined && x.poi_tip_id === 40));
          const featuresOpasnost = createPOIFeatures(resp.data.filter((x) => x.geom !== undefined && x.poi_tip_id === 50));
          const featuresVrh = createPOIFeatures(resp.data.filter((x) => x.geom !== undefined && x.poi_tip_id === 60));
          const featuresOdmoriste = createPOIFeatures(resp.data.filter((x) => x.geom !== undefined && x.poi_tip_id === 91));
          const featuresPolazista = createPOIFeatures(resp.data.filter((x) => x.geom !== undefined && x.poi_tip_id === 90));
          const featuresPrir = createPOIFeatures(
            resp.data.filter((x) => x.geom !== undefined && (x.poi_tip_id as number) >= 70 && (x.poi_tip_id as number) <= 73)
          );
          const featuresKult = createPOIFeatures(
            resp.data.filter((x) => x.geom !== undefined && (x.poi_tip_id as number) >= 81 && (x.poi_tip_id as number) <= 86)
          );
          const featuresHGSS = createPOIFeatures(resp.data.filter((x) => x.geom !== undefined && x.poi_tip_id === 20));
          const featuresObilaznicaKTs = resp.data
            .filter((x) => x.geom !== undefined && x.is_obilaznica_kt === true)
            .map((x) => {
              // @ts-ignore
              const f = (x.geom as OlFeature<Geometry>).clone();
              f.setId(x.id as number);
              return f;
            });

          const newPoiSources = [
            featuresPlaninarski,
            featuresSkloniste,
            featuresPenjaliste,
            featuresOpasnost,
            featuresVrh,
            featuresOdmoriste,
            featuresPolazista,
            featuresPrir,
            featuresKult,
            featuresHGSS,
            featuresObilaznicaKTs,
          ].map((x) => new OlSourceVector<OlFeature<Geometry>>({ features: x }));

          setPoiSources(newPoiSources);

          const layerPlanKuce = createPOILayerVector('poi-planinarski', 'map:layer.poi_plan_kuce_i_domovi', newPoiSources[0]);
          const layerPlanSklonista = createPOILayerVector('poi-sklonista', 'map:layer.poi_plan_sklonista', newPoiSources[1]);
          const layerPenjalista = createPOILayerVector('poi-penjalista', 'map:layer.poi_penjalista', newPoiSources[2]);
          // const layerOpasnost = createPOILayerVector("poi-opasnost", "map:layer.poi_opasnost", newPoiSources[3]);
          const layerVrhovi = createPOILayerVector('poi-vrh', 'map:layer.vrhovi', newPoiSources[4]);
          const layerOdmorista = createPOILayerVector('poi-odmorista', 'map:layer.odmorista', newPoiSources[5]);
          const layerPolazista = createPOILayerVector('poi-polazista', 'map:layer.polazista', newPoiSources[6]);
          const layerPrir = createPOILayerVector('poi-prir', 'map:layer.poi_prir', newPoiSources[7]);
          const layerKult = createPOILayerVector('poi-kult', 'map:layer.poi_kult', newPoiSources[8]);
          const layerHGSS = createPOILayerVector('poi-hgss', 'map:layer.poi_hgss', newPoiSources[9]);
          const layerObilaznicaKTs = createPOILayerVector('obilaznica-kts', 'map:layer.obilaznica_kts', newPoiSources[10]);

          const poiLayerGroup = new OlLayerGroup({
            layers: [
              layerPlanKuce,
              layerPlanSklonista,
              layerPenjalista,
              layerVrhovi,
              layerOdmorista,
              layerPolazista,
              layerPrir,
              layerKult,
              layerHGSS,
              layerObilaznicaKTs,
            ],
          });
          poiLayerGroup.set('id', 'poi');
          poiLayerGroup.set('title', 'map:layer.poi');
        }
      }
    });
  };

  const routeExtent = getRouteExtent();

  return (userContext && layersCollection ? (
    <Map
      mapId={mapId}
      height="400px"
      view={viewOptions}
      className="feature-map"
      id="feature-map"
      initialized={mapInitialized}
      moveTolerance={10}
      maxTilesLoading={10}
      zoomToExtentPadding={[20, 20, 20, 20]}
    >
      <Controls>
        <ZoomControl zoomInTipLabel={t('map:controls.zoom_in')} zoomOutTipLabel={t('map:controls.zoom_out')} />
        <ScaleLineControl type="minimap" />
        {routeExtent ?
          <ZoomToExtentControl
            id="zoom-extent-default"
            extent={routeExtent}
            tipLabel={t('map:controls.zoom_to_extent')}
            className='public-routemap-zoom'
          /> : null}
        <DisplayRouteControl id="disp-route" initialRecordRoutePoints={initialRecordRoutePoints} />
      </Controls>
      <Layers>
        <GeoAPIBaseLayers setBaseLayersCollection={setBaseLayersCollection} mapId={mapId} />
        {layersCollection ? (
          <>
            <ClientLayer
              id="poi-planinarski"
              source={poiSources ? poiSources[0] : new OlSourceVector<OlFeature<Geometry>>()}
              zIndex={708}
              style={poiStyle}
            />
            <ClientLayer
              id="poi-sklonista"
              source={poiSources ? poiSources[1] : new OlSourceVector<OlFeature<Geometry>>()}
              zIndex={707}
              style={poiStyle}
            />
            <ClientLayer
              id="poi-penjalista"
              source={poiSources ? poiSources[2] : new OlSourceVector<OlFeature<Geometry>>()}
              zIndex={706}
              style={poiStyle}
            />
            <ClientLayer
              id="poi-opasnost"
              source={poiSources ? poiSources[3] : new OlSourceVector<OlFeature<Geometry>>()}
              zIndex={705}
              style={poiStyle}
            />
            <ClientLayer
              id="poi-vrh"
              source={poiSources ? poiSources[4] : new OlSourceVector<OlFeature<Geometry>>()}
              zIndex={704}
              style={poiStyle}
              maxResolution={40}
            />
            <ClientLayer
              id="poi-odmorista"
              source={poiSources ? poiSources[5] : new OlSourceVector<OlFeature<Geometry>>()}
              zIndex={702}
              style={poiStyle}
            />
            <ClientLayer
              id="poi-polazista"
              source={poiSources ? poiSources[6] : new OlSourceVector<OlFeature<Geometry>>()}
              zIndex={701}
              style={poiStyle}
            />
            <ClientLayer
              id="poi-prir"
              source={poiSources ? poiSources[7] : new OlSourceVector<OlFeature<Geometry>>()}
              zIndex={699}
              style={poiStyle}
            />
            <ClientLayer
              id="poi-kult"
              source={poiSources ? poiSources[8] : new OlSourceVector<OlFeature<Geometry>>()}
              zIndex={698}
              style={poiStyle}
            />
            <ClientLayer
              id="poi-hgss"
              source={poiSources ? poiSources[9] : new OlSourceVector<OlFeature<Geometry>>()}
              zIndex={700}
              style={poiStyle}
            />
            <ClientLayer
              id="obilaznica-kts"
              source={poiSources ? poiSources[10] : new OlSourceVector<OlFeature<Geometry>>()}
              zIndex={710}
              style={ktStyle}
            />
            <GeoAPILayers layersCollection={layersCollection} />
            {/* <VectorLayer id="selected" source={selectedSource} style={selectedStyle} zIndex={800} /> */}
          </>
        ) : null}
      </Layers>
      <Overlays />
      <Interactions>
        <DefaultInteractions />
        {/* <MouseWheelZoomInteraction useAnchor={false} /> */}
      </Interactions>
    </Map>
  ) : null);
}

export default PublicRouteMap;
